/**
 * Command Center API for My guides v1: feature flags and Tripsby staff.
 *
 * A sibling of api-client.ts (which is at its structure budget) that reuses its
 * axios instance, so the same auth header, token refresh and error mapping apply.
 * The two new permission tabs, `feature_flags` and `staff`, are declared on
 * `UserPermissions` in api-client.ts.
 */

import { AxiosError } from 'axios';
import { APIErrorResponse, apiClient, handleApiError } from './api-client';

// Feature flags (settings/feature_flags in Firestore, read by Orchestrator).
export type FeatureFlagAudience = 'staff' | 'admin' | 'business' | 'traveller' | 'everyone';

export interface FeatureFlag {
  description: string;
  enabled: boolean;
  audiences: FeatureFlagAudience[];
  users: string[]; // Orchestrator user ids
  blockedUsers: string[]; // Orchestrator user ids
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface FeatureFlagsResponse {
  flags: Record<string, FeatureFlag>;
  updated_at?: string | null;
  updated_by?: string | null;
}

/** `enabled` is required; anything omitted keeps its stored value. */
export interface FeatureFlagInput {
  enabled: boolean;
  description?: string;
  audiences?: FeatureFlagAudience[];
  users?: string[];
  blockedUsers?: string[];
}

export interface FeatureFlagSaveResult {
  success: boolean;
  key: string;
  changed: boolean;
  flag: FeatureFlag;
}

// Staff tiers live in Orchestrator; Command Center reaches them through the backend proxy.
export type StaffTier = 'none' | 'staff' | 'admin';

export interface OrchestratorPerson {
  personId: string;
  name?: string | null;
  email?: string | null;
  /** Orchestrator's effective tier: 'none' for a staff role on a non-Tripsby email. */
  tier?: StaffTier | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  /** Verified tripsby.ai or byai.group email, so this person can be made staff. */
  eligibleForStaff: boolean;
}

type ApiError = AxiosError<APIErrorResponse>;

export const myGuidesAdminApi = {
  // ---------------------------------------------------------------------------
  // Feature flags. One flag per write; the backend audits before and after values.
  // ---------------------------------------------------------------------------

  async getFeatureFlags(): Promise<FeatureFlagsResponse> {
    try {
      const response = await apiClient.get<FeatureFlagsResponse>('/api/v1/feature-flags');
      return response.data;
    } catch (error) {
      throw handleApiError(error as ApiError);
    }
  },

  /** Create-or-update one flag (used by the edit drawer). */
  async updateFeatureFlag(key: string, input: FeatureFlagInput): Promise<FeatureFlagSaveResult> {
    try {
      const response = await apiClient.put<FeatureFlagSaveResult>(
        `/api/v1/feature-flags/${encodeURIComponent(key)}`,
        input
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error as ApiError);
    }
  },

  /** Create-only (used by "New flag"): a key that already exists is refused with 409. */
  async createFeatureFlag(key: string, input: FeatureFlagInput): Promise<FeatureFlagSaveResult> {
    try {
      const response = await apiClient.post<FeatureFlagSaveResult>('/api/v1/feature-flags', {
        key,
        ...input,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as ApiError);
    }
  },

  // ---------------------------------------------------------------------------
  // Staff tiers. Proxied by the backend to Orchestrator's M2M routes.
  // ---------------------------------------------------------------------------

  async getStaff(): Promise<OrchestratorPerson[]> {
    try {
      const response = await apiClient.get<{ staff: OrchestratorPerson[] }>('/api/v1/staff');
      return response.data.staff;
    } catch (error) {
      throw handleApiError(error as ApiError);
    }
  },

  async searchStaffPeople(email: string): Promise<OrchestratorPerson[]> {
    try {
      const response = await apiClient.get<{ users: OrchestratorPerson[] }>(
        '/api/v1/staff/users/search',
        { params: { email } }
      );
      return response.data.users;
    } catch (error) {
      throw handleApiError(error as ApiError);
    }
  },

  async getStaffPerson(personId: string): Promise<OrchestratorPerson> {
    try {
      const response = await apiClient.get<{ user: OrchestratorPerson }>(
        `/api/v1/staff/users/${encodeURIComponent(personId)}`
      );
      return response.data.user;
    } catch (error) {
      throw handleApiError(error as ApiError);
    }
  },

  /** Only Command Center admins may grant 'admin'; the backend refuses a developer with 403. */
  async setStaffTier(personId: string, tier: StaffTier): Promise<OrchestratorPerson | null> {
    try {
      const response = await apiClient.put<{ success: boolean; staff: OrchestratorPerson | null }>(
        `/api/v1/staff/${encodeURIComponent(personId)}`,
        { tier }
      );
      return response.data.staff;
    } catch (error) {
      throw handleApiError(error as ApiError);
    }
  },
};
